import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import {
  getClosestSites,
  getClosestSitesHourlyWashCounts,
  getDailyWashCounts,
  OVERVIEW_GET_CLOSEST_SITES,
  OVERVIEW_GET_CLOSEST_SITES_HOURLY_WASHCOUNTS,
  OVERVIEW_GET_DAILY_WASHCOUNTS,
} from '../actions';
import { selectMultiSiteSelection } from '../../site-selection/reducer';
import {
  selectClosestHourlyWashCounts,
  selectClosestSites,
  selectDailyWashCounts,
} from '../reducer';
import { selectSiteExternalId, selectManagerSiteId } from '../../settings/reducer';
import { useIntl } from 'react-intl';
import { getApiStatus } from '../../../core/utils';
import Loader from '../../../core/components/Loader';

interface WashCountTableProps {
  hourlyCounts: Record<string, HourlyWashCountReturn>;
}

interface HourlyWashCountReturn {
  count: number;
  countsBySiteId?: Record<string, number> | null;
  [key: string]: any;
}

interface DailyWashCountRow {
  siteId: number;
  count: number;
  category: string;
  [key: string]: any;
}

interface WashCountRow {
  hour: string;
  counts: number[];
}

export const WashCountTable = ({ hourlyCounts }: WashCountTableProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const startToday = moment().startOf('day').toISOString();
  const siteId = useSelector(selectManagerSiteId);
  const siteExternalId = useSelector(selectSiteExternalId);
  const closestSites = useSelector(selectClosestSites);
  const multiSiteSelection = useSelector(selectMultiSiteSelection);
  const closestHourlyWashCounts = useSelector(selectClosestHourlyWashCounts);
  const dailyWashCounts = useSelector(selectDailyWashCounts);
  const closestSitesLoading = useSelector((state) =>
    getApiStatus(state, OVERVIEW_GET_CLOSEST_SITES)
  );
  const hourlyWashCountsLoading = useSelector((state) =>
    getApiStatus(state, OVERVIEW_GET_CLOSEST_SITES_HOURLY_WASHCOUNTS)
  );
  const dailyWashCountsLoading = useSelector((state) =>
    getApiStatus(state, OVERVIEW_GET_DAILY_WASHCOUNTS)
  );
  const prevClosestSitesLength = useRef(-1);
  const closestSitesJustLoaded = prevClosestSitesLength.current !== closestSites.length;
  prevClosestSitesLength.current = closestSites.length;
  const isLoading =
    closestSitesLoading ||
    hourlyWashCountsLoading ||
    dailyWashCountsLoading ||
    closestSitesJustLoaded;

  const lastXHours = (x = 12) => {
    const hours = [];
    let currentHour = new Date().getHours();
    for (let i = 0; i < x; i++) {
      hours.push(currentHour);
      if (currentHour - 1 < 0) {
        return hours;
      } else {
        currentHour -= 1;
      }
    }
    return hours;
  };

  const transformHour = (hour: number) => {
    const h = hour % 12 || 12;
    const ampm = hour > 11 ? 'pm' : 'am';
    return `${h}${ampm}`;
  };

  const transformCounts = (counts: HourlyWashCountReturn) => {
    const siteCounts = counts?.countsBySiteId;
    return selectedSites.map((s: any) => siteCounts?.[s?.id] ?? 0);
  };

  const transformHourlyData = (data: Record<string, HourlyWashCountReturn> | null) => {
    const hours = lastXHours();
    return Object.entries(data ?? {})
      .filter(([key]) => hours.includes(Number(key)))
      .map(([key, value]) => ({
        hour: transformHour(Number(key)),
        counts: transformCounts(value),
      }));
  };

  const transformDailyData = (data: Record<string, DailyWashCountRow[]> | null) => {
    return selectedSites.map((s: any) => {
      const rows = (data ?? {})[s?.id] ?? [];
      return rows.reduce((sum, row) => sum + row.count, 0);
    });
  };

  const selectedSites = useMemo(() => {
    let sites: { id: string; externalId: string }[] = [];
    if (siteId) {
      sites.push({
        id: siteId,
        externalId: siteExternalId ?? intl.formatMessage({ id: 'mySite' }),
      });
    }
    const additionalSites = multiSiteSelection.length ? multiSiteSelection : closestSites;
    const additional = additionalSites.map((s: any) => ({
      id: s.id,
      externalId: s.externalId ?? s.id,
    }));
    if (additional.length) sites = sites.concat(additional);
    return sites;
  }, [siteId, siteExternalId, multiSiteSelection, closestSites]);

  const tableHeaders = useMemo(() => {
    return selectedSites.map((s: any) => s.externalId);
  }, [selectedSites]);

  const hourlyData = useMemo(() => {
    if (!multiSiteSelection.length && closestSites.length) {
      return closestHourlyWashCounts;
    }
    return hourlyCounts;
  }, [hourlyCounts, closestHourlyWashCounts, closestSites, multiSiteSelection]);

  useEffect(() => {
    if (closestSites.length) {
      dispatch(
        getClosestSitesHourlyWashCounts(
          moment(startToday).format('YYYY-MM-DD'),
          moment(startToday).format('YYYY-MM-DD'),
          null
        )
      );
    }
  }, [closestSites, multiSiteSelection]);

  useEffect(() => {
    const useClosestSites = !multiSiteSelection.length;
    if (siteId && (!useClosestSites || closestSites.length)) {
      dispatch(
        getDailyWashCounts(siteId, moment(startToday).format('YYYY-MM-DD'), useClosestSites)
      );
    }
  }, [siteId, multiSiteSelection, closestSites]);

  useEffect(() => {
    dispatch(getClosestSites({ showOperational: true }));
  }, [siteId]);

  return (
    <section className="todays-wash-counts overview-chart-card vertically-centered">
      <p className="chart-title">{intl.formatMessage({ id: 'todaysWashCounts' })}</p>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="wash-count-table-container">
          <div className="wash-count-table">
            <div className="wash-count-table-row header">
              <div></div>
              {tableHeaders.map((t: string, i: number) => (
                <div key={i}>{t}</div>
              ))}
            </div>
            {dailyWashCounts && (
              <div className="wash-count-table-row totals">
                <div>{intl.formatMessage({ id: 'total' })}</div>
                {transformDailyData(dailyWashCounts).map((count: number, i: number) => (
                  <div key={i}>{count}</div>
                ))}
              </div>
            )}
            {transformHourlyData(hourlyData).map((h: WashCountRow, i: number) => (
              <div className="wash-count-table-row" key={i}>
                <div>{h.hour}</div>
                {h.counts.map((c: number, j: number) => (
                  <div key={j}>{c}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
